// Icon strip for Messages / Ask / PA / Achievements + right-aligned alert icons.
import { useEffect, useState, type RefObject } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { usePaAssistMode } from "@/components/activity/personalAssistant/usePaAssistMode";
import { canShowPersonalAssistantToggle } from "@/lib/personalAssistant/paModeGate";
import { useWorkplaceStreamChatSession } from "@/contexts/WorkplaceStreamChatSessionContext";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { ActivityDockAskFirefly } from "@/components/activity/ActivityDockAskFirefly";
import type { ActivityDockAskFireflyHandle } from "@/components/activity/firefly/useActivityDockAskFireflyController";
import type { ActivityDockAskFireflyProps } from "@/components/activity/firefly/useActivityDockAskFireflyController";
import { ActivityDockMessagesSection } from "@/components/activity/sections/ActivityDockMessagesSection";
import { ActivityPanelAchievements } from "@/components/activity/ActivityPanelAchievements";
import { ActivityDockAlertIcons } from "@/components/activity/ActivityDockAlertIcons";
import {
  ACTIVITY_DOCK_OPEN_EVENT,
  takePendingDockFocusSection,
  takePendingExpandAsk,
  type ActivityDockOpenDetail,
} from "@/components/activity/activityDockOpenEvent";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useSidebarBreakpoint } from "@/components/shell/Sidebar/useSidebarBreakpoint";
import { dispatchMessengerOpen } from "@/components/shell/popovers/messengerOpenEvent";
import { setMessengerSizeMode } from "@/lib/messages/messengerSizePreference";

export type ActivityDockPrimaryTabId = "messages" | "ask" | "pa" | "achievements";

export type ActivityDockPrimaryTabsProps = {
  askRef?: RefObject<ActivityDockAskFireflyHandle | null>;
  askProps: Omit<ActivityDockAskFireflyProps, "presentation">;
  currentMode?: PrimaryMode | null;
  onCloseDock?: () => void;
};

export function ActivityDockPrimaryTabs({
  askRef,
  askProps,
  currentMode = null,
  onCloseDock,
}: ActivityDockPrimaryTabsProps) {
  const auth = useAuth();
  const hasCapability = useUserCapabilities();
  const { assistMode } = usePaAssistMode();
  const { level: assistLevel } = useActivityDockAssistLevel();
  const session = useWorkplaceStreamChatSession();
  const fresh = useFreshAchievementsOptional();
  const isMobile = useSidebarBreakpoint() === "mobile";
  const canShowPa = canShowPersonalAssistantToggle({
    authReady: auth.status === "ready",
    impersonationActive: auth.impersonation.active,
    personalAssistantEnabled: auth.personalAssistantEnabled,
    hasRole: (...roles: string[]) => auth.hasRole(...roles),
    hasPersonalAssistantCapability: hasCapability("is_personal_assistant"),
    assistMode,
  });
  const showAsk = assistLevel !== "off";
  const showMessages = session.vendorEnabled !== false;
  const freshCount = fresh?.queue.length ?? 0;

  // Prefer Ask over Messages unless an explicit messages deep-link is pending.
  const [tab, setTab] = useState<ActivityDockPrimaryTabId>(() => {
    if (takePendingExpandAsk()) return "ask";
    if (takePendingDockFocusSection() === "messages") return "messages";
    return "ask";
  });

  useEffect(() => {
    if (takePendingExpandAsk()) setTab("ask");
    else if (takePendingDockFocusSection() === "messages") setTab("messages");
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<ActivityDockOpenDetail>).detail;
      if (detail?.expandAsk) setTab("ask");
      else if (detail?.focusSection === "messages") setTab("messages");
    };
    window.addEventListener(ACTIVITY_DOCK_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(ACTIVITY_DOCK_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!showMessages && tab === "messages") {
      setTab(showAsk ? "ask" : canShowPa ? "pa" : "achievements");
    } else if (!showAsk && tab === "ask") {
      setTab(showMessages ? "messages" : canShowPa ? "pa" : "achievements");
    }
  }, [canShowPa, showAsk, showMessages, tab]);

  const unread = session.unreadCount;
  const bucketId = currentMode ? getBucketForMode(currentMode) : null;

  function openExpandedMessenger() {
    setMessengerSizeMode("expanded");
    dispatchMessengerOpen({ force: true });
    onCloseDock?.();
  }

  // Mobile: Messages always opens the shell fullscreen messenger (compact dock is unusable).
  useEffect(() => {
    if (!isMobile || !showMessages || tab !== "messages") return;
    openExpandedMessenger();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot on tab/mobile
  }, [isMobile, showMessages, tab]);

  return (
    <div className="mb-3">
      <div
        role="tablist"
        aria-label="Firefly primary"
        className="mb-2 flex items-center gap-1.5"
      >
        {showMessages ? (
          <PrimaryTabButton
            id="messages"
            label={unread > 0 ? `Messages, ${unread} unread` : "Messages"}
            active={tab === "messages"}
            onSelect={() => setTab("messages")}
            glyph={unread > 0 ? "💬" : "✉"}
            badge={unread > 0 ? (unread > 9 ? "9+" : String(unread)) : null}
          />
        ) : null}
        {showAsk ? (
          <PrimaryTabButton
            id="ask"
            label="Ask Firefly"
            active={tab === "ask"}
            onSelect={() => setTab("ask")}
            glyph="✦"
          />
        ) : null}
        {canShowPa && showAsk ? (
          <PrimaryTabButton
            id="pa"
            label="Personal Assistant"
            active={tab === "pa"}
            onSelect={() => setTab("pa")}
            glyph="PA"
          />
        ) : null}
        <PrimaryTabButton
          id="achievements"
          label={freshCount > 0 ? `Achievements, ${freshCount} new` : "Achievements"}
          active={tab === "achievements"}
          onSelect={() => setTab("achievements")}
          glyph="🏅"
          badge={freshCount > 0 ? (freshCount > 9 ? "9+" : String(freshCount)) : null}
          pulse={freshCount > 0}
        />
        {showMessages ? (
          <button
            type="button"
            onClick={openExpandedMessenger}
            aria-label="Expand messenger"
            title="Expand messenger"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            <span aria-hidden>⛶</span>
          </button>
        ) : null}
        <ActivityDockAlertIcons className="ml-auto" />
      </div>

      <div role="tabpanel" className="min-h-0">
        {tab === "messages" && showMessages && !isMobile ? (
          <ActivityDockMessagesSection presentation="pane" />
        ) : null}
        {tab === "messages" && showMessages && isMobile ? (
          <p className="px-1 py-2 text-[11px] text-gray-500 dark:text-zinc-400">
            Opening fullscreen messenger…
          </p>
        ) : null}
        {tab === "ask" && showAsk ? (
          <ActivityDockAskFirefly ref={askRef} {...askProps} presentation="pane-ask" />
        ) : null}
        {tab === "pa" && canShowPa && showAsk ? (
          <ActivityDockAskFirefly ref={askRef} {...askProps} presentation="pane-pa" />
        ) : null}
        {tab === "achievements" ? (
          <ActivityPanelAchievements bucketId={bucketId} />
        ) : null}
      </div>
    </div>
  );
}

function PrimaryTabButton(props: {
  id: string;
  label: string;
  active: boolean;
  onSelect: () => void;
  glyph: string;
  badge?: string | null;
  pulse?: boolean;
}) {
  const { id, label, active, onSelect, glyph, badge = null, pulse = false } = props;
  return (
    <button
      type="button"
      role="tab"
      id={`firefly-tab-${id}`}
      aria-selected={active}
      aria-label={label}
      title={label}
      onClick={onSelect}
      className={[
        "relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold transition-colors",
        active
          ? "bg-emerald-600 text-white"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-200 dark:hover:bg-zinc-700",
        pulse && !active ? "animate-pulse ring-2 ring-amber-400/80" : "",
      ].join(" ")}
    >
      <span aria-hidden>{glyph}</span>
      {badge ? (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-teal-700 px-0.5 text-[9px] font-bold text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
